import type { ReactNode } from "react";
import type { AsidePanelConfig, MainPanelConfig, TopbarConfig } from "@/panels/modules/panels/store/panels.store.types";
import { hasRenderableContent } from "@/utils/hasRenderableContent";

const COLLAPSED_HEIGHT_WITH_TABS = 90;
const COLLAPSED_HEIGHT_WITHOUT_TABS = 48;
const COLLAPSED_HEIGHT_WITH_TABS_NO_HEADER = 40;
const COLLAPSED_HEIGHT_WITHOUT_TABS_NO_HEADER = 0;

const MAIN_PANEL_MIN_SIZE = 10;
const DEFAULT_LEFT_SIDEBAR_SIZE = 15;
const DEFAULT_LEFT_SIDEBAR_MIN_WIDTH = "12rem";
const DEFAULT_LEFT_SIDEBAR_MAX_WIDTH = "15rem"; // 240 px when 1rem = 16 px
const DEFAULT_TOPBAR_SIZE = 7;
const DEFAULT_TOPBAR_MIN_HEIGHT = "100px"; // 100 px makes room for the scheduled callback indicator
const DEFAULT_RIGHT_SIDEBAR_SIZE = 18;
const DEFAULT_RIGHT_SIDEBAR_MIN_WIDTH = "17rem";
const DEFAULT_RIGHT_SIDEBAR_MAX_WIDTH = "18.75rem"; // 300 px when 1rem = 16 px

/** Panel sizes calculation helper - provides defaults when config values are not specified */
export const computePanelSizes = (
    leftSidebarConfig?: AsidePanelConfig,
    topbarConfig?: TopbarConfig,
    rightSidebarConfig?: AsidePanelConfig,
) => ({
    leftSidebar: {
        defaultSize: leftSidebarConfig?.defaultSize ?? DEFAULT_LEFT_SIDEBAR_SIZE,
        minWidth: leftSidebarConfig?.minWidth ?? DEFAULT_LEFT_SIDEBAR_MIN_WIDTH,
        maxWidth: leftSidebarConfig?.maxWidth ?? DEFAULT_LEFT_SIDEBAR_MAX_WIDTH,
    },
    topbar: {
        defaultSize: topbarConfig?.defaultHeight ?? DEFAULT_TOPBAR_SIZE,
        minHeight: topbarConfig?.minHeight ?? DEFAULT_TOPBAR_MIN_HEIGHT,
        maxHeight: topbarConfig?.maxHeight,
    },
    rightSidebar: {
        defaultSize: rightSidebarConfig?.defaultSize ?? DEFAULT_RIGHT_SIDEBAR_SIZE,
        minWidth: rightSidebarConfig?.minWidth ?? DEFAULT_RIGHT_SIDEBAR_MIN_WIDTH,
        maxWidth: rightSidebarConfig?.maxWidth ?? DEFAULT_RIGHT_SIDEBAR_MAX_WIDTH,
    },
    mainPanels: {
        minSize: MAIN_PANEL_MIN_SIZE,
    },
});

export const calculateTopPanelSize = (topConfig: MainPanelConfig, bottomConfig: MainPanelConfig): number => {
    const isTopSelectedTabMinimized = topConfig.selectedTab === "a" ? topConfig.a.isMinimized : topConfig.b.isMinimized;
    const isTopPanelClosed = topConfig.a.isClosed && topConfig.b.isClosed;

    const isBottomSelectedTabMinimized =
        bottomConfig.selectedTab === "a" ? bottomConfig.a.isMinimized : bottomConfig.b.isMinimized;
    const isBottomPanelClosed = bottomConfig.a.isClosed && bottomConfig.b.isClosed;

    if (isTopSelectedTabMinimized || isTopPanelClosed) {
        return 0;
    } else if (isBottomSelectedTabMinimized || isBottomPanelClosed) {
        return 100;
    } else {
        return 50;
    }
};

export const calculatePanelCollapsedHeightPixels = (
    config: MainPanelConfig,
    tabASlot: ReactNode,
    tabBSlot: ReactNode,
): number => {
    if (config.a.isClosed && config.b.isClosed) {
        return 0;
    }

    const bothTabsHaveContent = calcBothTabsHaveContent({
        tabASlot,
        isTabAClosed: config.a.isClosed,
        tabBSlot,
        isTabBClosed: config.b.isClosed,
    });

    return calcCollapsedHeightPixels(bothTabsHaveContent, Boolean(config[config.selectedTab].showHeader));
};

export const calcBothTabsHaveContent = ({
    tabASlot,
    isTabAClosed,
    tabBSlot,
    isTabBClosed,
}: {
    tabASlot: ReactNode;
    isTabAClosed: boolean;
    tabBSlot: ReactNode;
    isTabBClosed: boolean;
}): boolean => {
    const tabAHasContent = !isTabAClosed && hasRenderableContent(tabASlot);
    const tabBHasContent = !isTabBClosed && hasRenderableContent(tabBSlot);
    return tabAHasContent && tabBHasContent;
};

export const calcCollapsedHeightPixels = (bothTabsHaveContent: boolean, showHeader: boolean) => {
    return bothTabsHaveContent
        ? showHeader
            ? COLLAPSED_HEIGHT_WITH_TABS
            : COLLAPSED_HEIGHT_WITH_TABS_NO_HEADER
        : showHeader
          ? COLLAPSED_HEIGHT_WITHOUT_TABS
          : COLLAPSED_HEIGHT_WITHOUT_TABS_NO_HEADER;
};

// Helper functions to compute panel states from configs
export const isPanelFullyClosed = (config: MainPanelConfig): boolean => {
    return config.a.isClosed && config.b.isClosed;
};

export const isLeftMainPanelFullyClosed = (leftTop: MainPanelConfig, leftBottom: MainPanelConfig): boolean => {
    return isPanelFullyClosed(leftTop) && isPanelFullyClosed(leftBottom);
};

export const isOneLeftMainPanelFullyClosed = (leftTop: MainPanelConfig, leftBottom: MainPanelConfig): boolean => {
    return isPanelFullyClosed(leftTop) || isPanelFullyClosed(leftBottom);
};

export const isRightMainPanelFullyClosed = (rightTop: MainPanelConfig, rightBottom: MainPanelConfig): boolean => {
    return isPanelFullyClosed(rightTop) && isPanelFullyClosed(rightBottom);
};

export const isOneRightMainPanelFullyClosed = (rightTop: MainPanelConfig, rightBottom: MainPanelConfig): boolean => {
    return isPanelFullyClosed(rightTop) || isPanelFullyClosed(rightBottom);
};

export const isOneSideOfMainPanelsFullyClosed = (
    leftTop: MainPanelConfig,
    leftBottom: MainPanelConfig,
    rightTop: MainPanelConfig,
    rightBottom: MainPanelConfig,
): boolean => {
    return (
        (isPanelFullyClosed(leftTop) && isPanelFullyClosed(leftBottom)) ||
        (isPanelFullyClosed(rightTop) && isPanelFullyClosed(rightBottom))
    );
};

export const calculateLeftPanelSize = (
    isLeftMainPanelFullyClosed: boolean,
    isRightMainPanelFullyClosed: boolean,
): number => {
    if (isLeftMainPanelFullyClosed) return 0;

    return isRightMainPanelFullyClosed ? 100 : 50;
};

// Consolidated helper to compute all panel states and collapsed heights
export const computePanelStates = (
    leftTopConfig: MainPanelConfig,
    leftBottomConfig: MainPanelConfig,
    rightTopConfig: MainPanelConfig,
    rightBottomConfig: MainPanelConfig,
    leftTopA: ReactNode,
    leftTopB: ReactNode,
    leftBottomA: ReactNode,
    leftBottomB: ReactNode,
    rightTopA: ReactNode,
    rightTopB: ReactNode,
    rightBottomA: ReactNode,
    rightBottomB: ReactNode,
) => {
    // Panel state computations
    const isLeftMainPanelFullyClosedValue = isLeftMainPanelFullyClosed(leftTopConfig, leftBottomConfig);
    const isRightMainPanelFullyClosedValue = isRightMainPanelFullyClosed(rightTopConfig, rightBottomConfig);
    const isOneLeftMainPanelFullyClosedValue = isOneLeftMainPanelFullyClosed(leftTopConfig, leftBottomConfig);
    const isOneRightMainPanelFullyClosedValue = isOneRightMainPanelFullyClosed(rightTopConfig, rightBottomConfig);
    const isOneSideOfMainPanelsFullyClosedValue = isOneSideOfMainPanelsFullyClosed(
        leftTopConfig,
        leftBottomConfig,
        rightTopConfig,
        rightBottomConfig,
    );

    // Collapsed height computations
    const leftTopCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(leftTopConfig, leftTopA, leftTopB);
    const rightTopCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(rightTopConfig, rightTopA, rightTopB);
    const leftBottomCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(
        leftBottomConfig,
        leftBottomA,
        leftBottomB,
    );
    const rightBottomCollapsedHeightPixels = calculatePanelCollapsedHeightPixels(
        rightBottomConfig,
        rightBottomA,
        rightBottomB,
    );

    return {
        // Panel states
        isLeftMainPanelFullyClosedValue,
        isRightMainPanelFullyClosedValue,
        isOneLeftMainPanelFullyClosedValue,
        isOneRightMainPanelFullyClosedValue,
        isOneSideOfMainPanelsFullyClosedValue,

        // Collapsed heights
        leftTopCollapsedHeightPixels,
        rightTopCollapsedHeightPixels,
        leftBottomCollapsedHeightPixels,
        rightBottomCollapsedHeightPixels,
    };
};
